import React, { useEffect, useRef, useState } from 'react';
import { Plus, X, Loader2, Camera, Image as ImageIcon } from 'lucide-react';
import { compressImage, formatSize } from '../../utils/file';

const MAX_BYTES = 2 * 1024 * 1024;
const STEPS = [
  { maxDim: 2000, quality: 0.85 },
  { maxDim: 1600, quality: 0.75 },
  { maxDim: 1280, quality: 0.7 },
  { maxDim: 1024, quality: 0.6 },
];

const shrinkToLimit = async (file) => {
  if (file.size <= MAX_BYTES) return file;
  let out = file;
  for (const opts of STEPS) {
    out = await compressImage(file, opts);
    if (out.size <= MAX_BYTES) return out;
  }
  return out;
};

const Thumb = ({ file }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url ? <img src={url} alt="" className="w-7 h-7 object-cover rounded-sm border" /> : null;
};

export const KycUploadButton = ({ label, file, onChange, testId, width = 150 }) => {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const open = (ref) => { setMenu(false); ref.current?.click(); };

  const pick = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    if (!raw.type.startsWith('image/')) { setError('Please choose an image file'); return; }
    setBusy(true);
    const out = await shrinkToLimit(raw);
    setBusy(false);
    if (out.size > MAX_BYTES) { setError('File too large (Max 2MB)'); return; }
    setError('');
    onChange(out);
  };

  return (
    <div style={{ width }} className="relative" data-testid={`${testId}-wrapper`}>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testId}-camera-input`} />
      <button
        type="button"
        onClick={() => setMenu((m) => !m)}
        disabled={busy}
        className="w-full h-[36px] whitespace-nowrap rounded-[4px] bg-[#F7843A] hover:bg-[#EF7300] text-[#1a1a1a] font-bold text-[13px] tracking-[0.2em] flex items-center justify-center gap-2 active:scale-[0.98] transition-[background-color,transform]"
        data-testid={`${testId}-button`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{label} <Plus className="w-4 h-4" strokeWidth={3} /></>}
      </button>
      {menu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
          <div className="absolute left-0 right-0 top-[40px] z-20 bg-white border border-[#eee] rounded-md shadow-lg overflow-hidden text-[13px]" data-testid={`${testId}-menu`}>
            <button type="button" onClick={() => open(cameraRef)} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#FFF3E8]" data-testid={`${testId}-take-photo`}>
              <Camera className="w-4 h-4 text-[#EF7300]" /> Take Photo
            </button>
            <button type="button" onClick={() => open(inputRef)} className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-[#f0f0f0] hover:bg-[#FFF3E8]" data-testid={`${testId}-choose-gallery`}>
              <ImageIcon className="w-4 h-4 text-[#EF7300]" /> Choose Photo
            </button>
          </div>
        </>
      )}
      {file ? (
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[#333]" data-testid={`${testId}-selected`}>
          <Thumb file={file} />
          <span className="truncate flex-1" title={file.name}>{file.name} ({formatSize(file.size)})</span>
          <button type="button" onClick={() => onChange(null)} aria-label="Remove file" data-testid={`${testId}-remove`}>
            <X className="w-3.5 h-3.5 text-[#999] hover:text-[#E3473B]" />
          </button>
        </div>
      ) : (
        <p className="mt-1.5 text-[11px] text-[#9a9a9a] leading-tight">Upload supported file<br />(Max 2MB)</p>
      )}
      {error && <p className="text-[11px] text-[#E3473B] mt-0.5" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
};
